/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import React, { useEffect, useMemo, useRef, useState } from "react";

import { useDeviceType } from "../shared/hooks/useDeviceType";
import {
  CustomPlayerProps,
  MediaStreamType,
  PlayerLiveScreensConfig,
  PlayerMedia,
  PlayerPlayInfo,
} from "../shared/types";
import { isScheduledLiveScreenMediaType } from "../shared/utils/media";

import {
  BasePlayer,
  PlayerFeaturesProvider,
  PlayerSettingsProvider,
} from "./BasePlayer";
import {
  PlayerCurrentMediaProvider,
  VideoPlayerController,
} from "./Controller";
import {
  CustomPlayerContextProvider,
  CustomPlayerContextValue,
} from "./CustomPlayerContext";
import { LiveScreen } from "./LiveScreen";
import { Overlay } from "./Overlay";

type LivePlaybackState = "blocked" | "starting" | "live";

const getDateTime = (date?: Date | string): number | undefined => {
  if (!date) {
    return undefined;
  }

  const time = new Date(date).getTime();
  return Number.isNaN(time) ? undefined : time;
};

const isLiveMedia = (media: PlayerMedia, playInfo?: PlayerPlayInfo): boolean =>
  isScheduledLiveScreenMediaType(media.MediaTypeCode) ||
  isScheduledLiveScreenMediaType(playInfo?.MediaTypeCode);

const getLivePlaybackState = (
  media: PlayerMedia,
  playInfo: PlayerPlayInfo | undefined,
  liveScreens: PlayerLiveScreensConfig | undefined,
  now: number,
): LivePlaybackState => {
  const startTime = getDateTime(media.StartDateTime);

  if (!liveScreens || !isLiveMedia(media, playInfo) || !startTime) {
    return "live";
  }

  if (now >= startTime) {
    return "live";
  }

  if (!liveScreens.enablePreLiveAccess) {
    return "live";
  }

  const accessTimeMs =
    Math.max(liveScreens.preLiveAccessTimeMinutes ?? 0, 0) * 60 * 1000;
  const accessStartTime = startTime - accessTimeMs;

  return now >= accessStartTime ? "starting" : "blocked";
};

const CustomVideoPlayer = (props: CustomPlayerProps) => {
  const {
    media,
    playInfo,
    streamType = MediaStreamType.Main,
    isLoggedIn,
    renderLoginRequired,
    skipContinueWatching,
    onFetchMedia,
    onNavigateToMedia,
    onAnalyticsEvent,
    onPlayerMount,
    onLoadStartPosition,
    getCurrentTimeMs,
    chromecastReceiverId,
    withCredentials,
    sentryDsn,
    liveTuning,
    liveScreens,
    plugins,
    autoPlay = false,
  } = props;

  const { iPhoneDevice } = useDeviceType();
  const [now, setNow] = useState(() => getCurrentTimeMs?.() ?? Date.now());
  const playerMountCalledRef = useRef(false);
  const wasPreLiveVisibleRef = useRef(false);

  const customContext = useMemo<CustomPlayerContextValue>(
    () => ({
      onFetchMedia,
      onNavigateToMedia,
      onAnalyticsEvent,
      onLoadStartPosition,
      onPlayerMount,
      skipContinueWatching,
      chromecastReceiverId,
      withCredentials,
      sentryDsn,
      liveTuning,
      liveScreens,
      plugins,
    }),
    [
      onFetchMedia,
      onNavigateToMedia,
      onAnalyticsEvent,
      onLoadStartPosition,
      onPlayerMount,
      skipContinueWatching,
      chromecastReceiverId,
      withCredentials,
      sentryDsn,
      liveTuning,
      liveScreens,
      plugins,
    ],
  );

  const livePlaybackState = useMemo(
    () => getLivePlaybackState(media, playInfo, liveScreens, now),
    [liveScreens, media, now, playInfo],
  );

  useEffect(() => {
    const startTime = getDateTime(media?.StartDateTime);

    if (!startTime || (getCurrentTimeMs?.() ?? Date.now()) >= startTime) {
      return;
    }

    const intervalId = window.setInterval(() => {
      const nextNow = getCurrentTimeMs?.() ?? Date.now();
      setNow(nextNow);

      if (nextNow >= startTime) {
        window.clearInterval(intervalId);
      }
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [getCurrentTimeMs, media?.StartDateTime]);

  useEffect(() => {
    if (
      livePlaybackState === "live" &&
      playInfo &&
      !playerMountCalledRef.current
    ) {
      playerMountCalledRef.current = true;
      onPlayerMount?.();
    }
  }, [livePlaybackState, onPlayerMount, playInfo]);

  if (!media) {
    return null;
  }

  if (
    streamType !== MediaStreamType.Trial &&
    !isLoggedIn &&
    renderLoginRequired
  ) {
    return <>{renderLoginRequired()}</>;
  }

  if (livePlaybackState === "starting" && liveScreens) {
    wasPreLiveVisibleRef.current = true;
    return (
      <LiveScreen
        type="starting"
        config={liveScreens}
        startDateTime={media.StartDateTime}
        getCurrentTimeMs={getCurrentTimeMs}
      />
    );
  }

  if (livePlaybackState === "blocked" || !playInfo) {
    return null;
  }

  return (
    <CustomPlayerContextProvider value={customContext}>
      <PlayerFeaturesProvider
        autoPlay={autoPlay || wasPreLiveVisibleRef.current}
        continueWatching
        nextEpisode
        trackAnalytics
        // ads on iphone device does not work
        withClientSideAds={!iPhoneDevice}
      >
        <PlayerSettingsProvider>
          <BasePlayer>
            <PlayerCurrentMediaProvider
              media={media}
              playInfo={playInfo}
              streamType={streamType}
            >
              <VideoPlayerController>
                <Overlay />
              </VideoPlayerController>
            </PlayerCurrentMediaProvider>
          </BasePlayer>
        </PlayerSettingsProvider>
      </PlayerFeaturesProvider>
    </CustomPlayerContextProvider>
  );
};

export default CustomVideoPlayer;
